/* Options → Trade Log sub-tab. CRUD table over /api/trading/options_trades.
   Same code as the previous inline TradeLog in Options.jsx — zero
   functional changes, just extracted into its own module. */

import { useState } from "react";
import { C, Pill, Badge } from "./_shared";
import { STRATEGIES, BIASES, apiJSON, F, S } from "./_optionsShared";

export default function OptionsTradeLog({ trades, refreshTrades }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(null);

  async function del(id) {
    if (!window.confirm("Delete this options trade?")) return;
    try {
      await apiJSON(`/api/trading/options_trades/${id}`, { method: "DELETE" });
      refreshTrades();
    } catch { /* noop */ }
  }
  async function save() {
    try {
      const body = { ...form, entry_debit: +form.entry_debit || 0, qty: +form.qty || 1,
        buy_strike: +form.buy_strike || 0, sell_strike: +form.sell_strike || 0,
        entry_trigger: +form.entry_trigger || 0, max_risk: +form.max_risk || 0,
        profit_target: +form.profit_target || 0, stop_price: +form.stop_price || 0,
        exit_price: +form.exit_price || 0, net_pl: +form.net_pl || 0 };
      if (form.id) await apiJSON(`/api/trading/options_trades/${form.id}`, { method: "PUT", body: JSON.stringify(body) });
      else         await apiJSON(`/api/trading/options_trades`, { method: "POST", body: JSON.stringify(body) });
      setAdding(false); setForm(null); refreshTrades();
    } catch (e) { alert("Save failed: " + e.message); }
  }

  const closed = trades.filter(t => t.status === "Closed" || t.status === "Stop Hit");
  const totalPL = closed.reduce((a, t) => a + (+t.net_pl || 0), 0);
  const wins = closed.filter(t => (+t.net_pl || 0) > 0);
  const winRate = closed.length ? Math.round(wins.length / closed.length * 100) : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => { setAdding(true); setForm({ date: new Date().toISOString().slice(0,10), ticker: "", bias: "Bullish", strategy: "Call Debit Spread", expiration: "", entry_trigger: "", buy_strike: "", sell_strike: "", entry_debit: "", qty: "1", max_risk: "", profit_target: "", stop_price: "", exit_date: "", exit_price: "", net_pl: "", status: "Open", notes: "" }); }}
          data-testid="options-log-add-btn"
          style={{ padding: "7px 14px", borderRadius: 7, background: C.indigo, color: "#fff", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Add Trade</button>
        <span style={{ fontSize: 11, color: C.gray }}>{trades.length} trades · {closed.length} closed</span>
        <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: totalPL >= 0 ? C.green : C.red }}>
          Total P/L: {totalPL >= 0 ? "+" : ""}${totalPL.toFixed(0)} · Win rate: {winRate}%
        </span>
      </div>

      {adding && form && (
        <div style={{ background: "#fff", border: `1px solid ${C.indigo}44`, borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
            {F({ label: "Date", value: form.date, onChange: v => setForm(f => ({ ...f, date: v })), type: "date" })}
            {F({ label: "Ticker", value: form.ticker, onChange: v => setForm(f => ({ ...f, ticker: v.toUpperCase() })), placeholder: "MSFT" })}
            {S({ label: "Bias", value: form.bias, onChange: v => setForm(f => ({ ...f, bias: v })), options: BIASES })}
            {S({ label: "Strategy", value: form.strategy, onChange: v => setForm(f => ({ ...f, strategy: v })), options: STRATEGIES })}
            {F({ label: "Expiration", value: form.expiration, onChange: v => setForm(f => ({ ...f, expiration: v })), type: "date" })}
            {F({ label: "Entry Trigger", value: form.entry_trigger, onChange: v => setForm(f => ({ ...f, entry_trigger: v })), type: "number" })}
            {F({ label: "Buy Strike", value: form.buy_strike, onChange: v => setForm(f => ({ ...f, buy_strike: v })), type: "number" })}
            {F({ label: "Sell Strike", value: form.sell_strike, onChange: v => setForm(f => ({ ...f, sell_strike: v })), type: "number" })}
            {F({ label: "Entry Debit/Credit", value: form.entry_debit, onChange: v => setForm(f => ({ ...f, entry_debit: v })), type: "number" })}
            {F({ label: "Qty", value: form.qty, onChange: v => setForm(f => ({ ...f, qty: v })), type: "number" })}
            {F({ label: "Max Risk", value: form.max_risk, onChange: v => setForm(f => ({ ...f, max_risk: v })), type: "number" })}
            {F({ label: "Profit Target", value: form.profit_target, onChange: v => setForm(f => ({ ...f, profit_target: v })), type: "number" })}
            {F({ label: "Stop Price", value: form.stop_price, onChange: v => setForm(f => ({ ...f, stop_price: v })), type: "number" })}
            {F({ label: "Exit Date", value: form.exit_date, onChange: v => setForm(f => ({ ...f, exit_date: v })), type: "date" })}
            {F({ label: "Exit Price", value: form.exit_price, onChange: v => setForm(f => ({ ...f, exit_price: v })), type: "number" })}
            {F({ label: "Net P/L", value: form.net_pl, onChange: v => setForm(f => ({ ...f, net_pl: v })), type: "number" })}
            {S({ label: "Status", value: form.status, onChange: v => setForm(f => ({ ...f, status: v })), options: ["Open", "Closed", "Stop Hit"] })}
          </div>
          <div style={{ marginTop: 8 }}>
            {F({ label: "Notes", value: form.notes, onChange: v => setForm(f => ({ ...f, notes: v })), placeholder: "What happened..." })}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={save} data-testid="options-log-save-btn"
              style={{ padding: "7px 16px", borderRadius: 7, background: C.indigo, color: "#fff", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Save</button>
            <button onClick={() => { setAdding(false); setForm(null); }}
              style={{ padding: "7px 12px", borderRadius: 7, background: C.gbg, color: C.gray, border: "none", fontSize: 12, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
        {trades.length === 0 ? (
          <div data-testid="options-log-empty" style={{ padding: "32px 16px", textAlign: "center", fontSize: 12, color: C.gray }}>
            No options trades logged yet. Plan a trade above or click + Add Trade.
          </div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 1000 }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {["Date","Ticker","Bias","Strategy","Exp","Trigger","Buy","Sell","Debit","Qty","Risk","Exit","P/L","Status",""].map(h => (
                    <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: C.gray, fontWeight: 500, fontSize: 10, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trades.map(t => {
                  const pnl = +t.net_pl || 0;
                  return (
                    <tr key={t.id} data-testid={`options-row-${t.ticker}`} style={{ borderTop: "0.5px solid #f3f4f6" }}>
                      <td style={{ padding: "6px 8px", color: C.gray, whiteSpace: "nowrap" }}>{t.date}</td>
                      <td style={{ padding: "6px 8px", fontWeight: 800, color: "#111" }}>{t.ticker}</td>
                      <td style={{ padding: "6px 8px" }}><Pill label={t.bias} bg={t.bias === "Bullish" ? C.gb : t.bias === "Bearish" ? C.rb : C.gbg} text={t.bias === "Bullish" ? C.gt : t.bias === "Bearish" ? C.rt : C.gt2}/></td>
                      <td style={{ padding: "6px 8px", fontSize: 10, color: C.indigo, fontWeight: 500, whiteSpace: "nowrap" }}>{t.strategy}</td>
                      <td style={{ padding: "6px 8px", color: C.gray }}>{t.expiration || "—"}</td>
                      <td style={{ padding: "6px 8px" }}>${(+t.entry_trigger || 0).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px" }}>{t.buy_strike}</td>
                      <td style={{ padding: "6px 8px" }}>{t.sell_strike}</td>
                      <td style={{ padding: "6px 8px" }}>${(+t.entry_debit || 0).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px" }}>{t.qty}</td>
                      <td style={{ padding: "6px 8px", color: C.red }}>${(+t.max_risk || 0).toFixed(0)}</td>
                      <td style={{ padding: "6px 8px" }}>{t.exit_price ? `$${(+t.exit_price).toFixed(2)}` : <span style={{ color: C.amber }}>Open</span>}</td>
                      <td style={{ padding: "6px 8px", fontWeight: 700, color: pnl >= 0 ? C.green : C.red }}>{t.status !== "Open" ? `${pnl >= 0 ? "+" : ""}$${pnl.toFixed(0)}` : "—"}</td>
                      <td style={{ padding: "6px 8px" }}><Badge label={t.status} bg={t.status === "Open" ? C.ab : t.status === "Closed" ? C.gb : C.rb} text={t.status === "Open" ? C.at : t.status === "Closed" ? C.gt : C.rt} size={10}/></td>
                      <td style={{ padding: "6px 8px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => { setForm({ ...t }); setAdding(true); }} data-testid={`options-edit-${t.ticker}`}
                            style={{ fontSize: 10, padding: "2px 7px", borderRadius: 5, border: "0.5px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", color: C.gray }}>Edit</button>
                          <button onClick={() => del(t.id)} data-testid={`options-del-${t.ticker}`}
                            style={{ fontSize: 10, padding: "2px 7px", borderRadius: 5, border: "none", background: C.rb, cursor: "pointer", color: C.rt }}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
